import { useMemo } from "react";

import type {
  ChartDimensions,
  PositionPayGapData,
} from "../PaygapPayGradesOverview";

// Chart dimension constants
const MIN_BAR_WIDTH = 60;
const MAX_BAR_WIDTH = 200;
const BAR_SPACING = 20;
const MIN_CHART_WIDTH = 400;
const MAX_CHART_WIDTH = 1400;
const LEFT_PADDING = 140;
const RIGHT_PADDING = 60;

export const CHART_HEIGHT = 650;
export const SIDE_PADDING = LEFT_PADDING + RIGHT_PADDING;

interface UseChartDimensionsReturn extends ChartDimensions {
  leftPadding: number;
  rightPadding: number;
  barSpacing: number;
}

/**
 * Hook to calculate optimal chart dimensions based on data length
 */
export const useChartDimensions = (
  data: PositionPayGapData[],
): UseChartDimensionsReturn => {
  const { barWidth, chartWidth } = useMemo((): ChartDimensions => {
    const dataCount = data.length || 1;

    // Calculate available width (excluding padding)
    const availableWidth = MAX_CHART_WIDTH - SIDE_PADDING;

    // Calculate optimal bar width (wider for fewer items, narrower for more)
    const optimalBarWidth = Math.max(
      MIN_BAR_WIDTH,
      Math.min(MAX_BAR_WIDTH, availableWidth / dataCount - BAR_SPACING),
    );

    // Calculate total chart width needed
    const totalBarsWidth = dataCount * optimalBarWidth;
    const totalSpacingWidth = (dataCount - 1) * BAR_SPACING;
    const calculatedWidth = totalBarsWidth + totalSpacingWidth + SIDE_PADDING;

    // Use calculated width but ensure it's within min/max bounds
    const finalChartWidth = Math.max(
      MIN_CHART_WIDTH,
      Math.min(MAX_CHART_WIDTH, calculatedWidth),
    );

    // Recalculate bar width to fit the final chart width
    const finalAvailableWidth = finalChartWidth - SIDE_PADDING;
    const finalBarWidth = Math.max(
      MIN_BAR_WIDTH,
      Math.min(MAX_BAR_WIDTH, (finalAvailableWidth - totalSpacingWidth) / dataCount),
    );

    return {
      barWidth: finalBarWidth,
      chartWidth: finalChartWidth,
    };
  }, [data.length]);

  return {
    barWidth,
    chartWidth,
    leftPadding: LEFT_PADDING,
    rightPadding: RIGHT_PADDING,
    barSpacing: BAR_SPACING,
  };
};

/**
 * Calculate dynamic max value based on data for Y-axis
 */
export const useMaxValue = (data: PositionPayGapData[]): number => {
  return useMemo(() => {
    if (data.length === 0) return 15;
    const maxAbsValue = Math.max(...data.map((item) => Math.abs(item.payGap)));
    // Add 5% buffer and round up for nice grid lines
    const bufferedMax = maxAbsValue * 1.05;
    // For small values, round to nearest 5; for large values, round to significant figures

    if (bufferedMax < 100) {
      return Math.ceil(bufferedMax / 5) * 5 || 15;
    }
    // Round up to 2 significant figures for large numbers
    const magnitude = Math.pow(10, Math.floor(Math.log10(bufferedMax)));


return Math.ceil(bufferedMax / magnitude) * magnitude;
  }, [data]);
};
